import React, { useState, useMemo } from 'react';
import { 
    Search, Eye,  Info, ShieldAlert, CheckCircle, 
    Grid, List, ChevronLeft, ChevronRight, X,
    FileText, HelpCircle,  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ZONE_DATA = [
    {
        code: "R-1",
        name: "Single-Family Residential",
        category: "Residential",
        uses: ["Detached Single-Family Dwellings", "ADUs", "JADUs", "Home Occupations", "Duplexes (SB 9)"],
        description: "Low-density residential zones designed for detached single-family houses. Focuses on preserving neighborhood character, managing lot coverage, and enforcing standard yard setbacks.",
        details: "Requires standard front (20-30 ft), rear (15-25 ft), and side (5-10 ft) setbacks. Highly restricted for non-residential activities.",
        cities: "All 92 jurisdictions (e.g., Palo Alto, Los Altos, Los Altos Hills, Saratoga, Monte Sereno).",
        mfa: "3,000 - 5,000 sq ft (Varies by lot size)",
        setbacks: { front: "20-30ft", rear: "15-25ft", side: "5-10ft" },
        height: "28-30 ft",
        lotCoverage: "30-40%"
    },
    {
        code: "R-2",
        name: "Two-Family Residential / Duplex",
        category: "Residential",
        uses: ["Duplexes", "Two detached dwellings", "Single-Family Dwellings", "ADUs", "JADUs"],
        description: "Medium-density residential districts designed to accommodate duplexes, triplexes, or two detached units on a single parcel. Serves as a transitional buffer zone.",
        details: "Typically allows slightly higher lot coverage (35-45%) and higher floor area ratios (FAR) than R-1 zones.",
        cities: "Palo Alto, Mountain View, Sunnyvale, Fremont, Oakland, San Jose, Redwood City.",
        mfa: "4,000 - 6,000 sq ft (Higher allowance)",
        setbacks: { front: "20ft", rear: "15ft", side: "5-8ft" },
        height: "28-30 ft",
        lotCoverage: "35-45%"
    },
    {
        code: "RM",
        name: "Multi-Family Residential",
        category: "Residential",
        uses: ["Apartments", "Condominiums", "Townhouses", "Multi-Family Complexes", "ADUs"],
        description: "High-density residential districts permitting multiple attached dwellings. Features tiered designations based on density limits (e.g., RM-15, RM-20).",
        details: "Allows high lot coverage (40-60%) and flat FAR structures. Requires architectural and design review.",
        cities: "Palo Alto, Los Gatos, Campbell, Sunnyvale, Mountain View, San Jose, Fremont.",
        mfa: "FAR Based (e.g. 0.6 - 1.0 FAR)",
        setbacks: { front: "15ft", rear: "15ft", side: "10ft" },
        height: "35-45 ft",
        lotCoverage: "40-60%"
    },
    {
        code: "RMD",
        name: "Residential Multiple Duplex",
        category: "Residential",
        uses: ["Duplexes", "Attached Townhomes", "Single-Family Dwellings", "ADUs", "JADUs"],
        description: "A specialized multi-family district designed specifically for low-profile, medium-density duplex and triplex dwellings.",
        details: "Applies flat FAR limits (e.g. 0.50 FAR in Palo Alto) and moderate site coverage constraints (35%).",
        cities: "Palo Alto, Menlo Park, Redwood City, Burlingame.",
        mfa: "Flat FAR limits (e.g. 0.50 FAR)",
        setbacks: { front: "20ft", rear: "20ft", side: "8ft" },
        height: "30 ft",
        lotCoverage: "35%"
    },
    {
        code: "RHS",
        name: "Residential Hillside Single-family",
        category: "Residential",
        uses: ["Hillside Single-Family Homes", "Agricultural Accessory Uses", "ADUs", "Wildlife Corridors"],
        description: "Specialized low-density zoning for steep terrain and hillside environments. Enforces strict slope-density formulas.",
        details: "Commonly triggers mandatory geotechnical reports, environmental reviews, and enhanced setbacks if average slope exceeds 10-15%.",
        cities: "Cupertino, Saratoga, Los Gatos, Los Altos Hills, Orinda, Lafayette.",
        mfa: "Slope-adjusted (Decreases rapidly with slope)",
        setbacks: { front: "30ft", rear: "30ft", side: "15-20ft" },
        height: "25-28 ft",
        lotCoverage: "10-20%"
    },
    {
        code: "RR",
        name: "Rural Residential",
        category: "Residential",
        uses: ["Estate Single-Family Dwellings", "Hobby Farms", "Horse Stables & Barns", "Private Orchards"],
        description: "Very low-density zoning designed for semi-rural and estate areas. Requires large minimum parcel sizes (typically 1 to 5+ acres).",
        details: "Strict lot coverage limits (10-20%) to preserve open space and permeable surfaces. Large side and rear setbacks are standard.",
        cities: "Los Altos Hills, Woodside, Portola Valley, Saratoga, Arnold.",
        mfa: "4,000 - 8,000+ sq ft (Estate size)",
        setbacks: { front: "50ft", rear: "50ft", side: "20ft" },
        height: "28-35 ft",
        lotCoverage: "10-20%"
    },
    {
        code: "RA",
        name: "Residential Agricultural",
        category: "Agricultural",
        uses: ["Single-Family Dwellings", "Small-scale Agriculture", "Greenhouses", "Stables"],
        description: "A hybrid zoning district that combines suburban residential living with light agricultural activities.",
        details: "Generous setback requirements to keep agricultural structures and animal pens separated from residential dwellings.",
        cities: "Arnold, Hillsborough, Woodside, Lafayette, Walnut Creek.",
        mfa: "3,000 - 6,000 sq ft",
        setbacks: { front: "30ft", rear: "25ft", side: "15ft" },
        height: "30 ft",
        lotCoverage: "20-30%"
    },
    {
        code: "CC",
        name: "Community Commercial",
        category: "Commercial",
        uses: ["Retail Stores", "Restaurants", "Offices", "Mixed-Use Residential"],
        description: "Standard commercial district intended for retail, service, and office establishments that serve the wider community.",
        details: "Allows high lot coverage (50-100%) and significant building height limits. Front and side setbacks are usually 0 ft except where abutting residential.",
        cities: "Palo Alto, Los Altos, Sunnyvale, Mountain View, San Jose, Fremont.",
        mfa: "Up to 2.0+ FAR",
        setbacks: { front: "0ft", rear: "0-10ft", side: "0ft" },
        height: "35-50+ ft",
        lotCoverage: "80-100%"
    },
    {
        code: "CN",
        name: "Neighborhood Commercial",
        category: "Commercial",
        uses: ["Neighborhood Retail", "Dry Cleaners", "Local Cafes", "Daycare Centers"],
        description: "Low-intensity commercial zone designed to provide daily goods and services within walking distance of surrounding residential neighborhoods.",
        details: "Typical FAR is low (0.35-0.40) and building heights are restricted (usually 30-35 ft) to match neighborhood scale.",
        cities: "Palo Alto, Los Altos, Cupertino, Campbell, San Jose, Oakland.",
        mfa: "0.35 - 0.50 FAR",
        setbacks: { front: "10ft", rear: "10ft", side: "5ft" },
        height: "30-35 ft",
        lotCoverage: "50-60%"
    },
    {
        code: "CS",
        name: "Service Commercial",
        category: "Commercial",
        uses: ["Auto Repair Shops", "Wholesale Facilities", "Light Custom Manufacturing", "Service Stations"],
        description: "A district reserved for service-oriented commercial activities that may involve noise, outdoor storage, or heavy vehicle traffic.",
        details: "Allows up to 50-60% lot coverage and flat FAR limits. Requires screening and landscape buffers when adjacent to residential districts.",
        cities: "Palo Alto, Mountain View, San Jose, Fremont, Alameda.",
        mfa: "0.50 - 1.0 FAR",
        setbacks: { front: "10ft", rear: "15ft", side: "0-10ft" },
        height: "35-40 ft",
        lotCoverage: "50-60%"
    },
    {
        code: "OS",
        name: "Open Space",
        category: "Special",
        uses: ["Nature Reserves", "Public Parks & Trails", "Watershed Protection", "Scientific Research"],
        description: "Conservation zone dedicated to preserving natural resources, scenic hillsides, parks, public beaches, and wildlife corridors.",
        details: "Highly restrictive. Development is generally limited to public parks infrastructure, trail maintenance, and essential agricultural structures.",
        cities: "All 92 jurisdictions (e.g., Los Altos Hills, Woodside, Portola Valley, Belvedere).",
        mfa: "N/A (Highly Restricted)",
        setbacks: { front: "N/A", rear: "N/A", side: "N/A" },
        height: "N/A",
        lotCoverage: "0-5%"
    },
    {
        code: "R-3",
        name: "Medium-High Density Residential",
        category: "Residential",
        uses: ["Apartments", "Condominiums", "Townhomes", "Boarding Houses", "ADUs"],
        description: "Residential district intended for higher density living. Typically situated near transit corridors or commercial centers.",
        details: "Allows greater floor area and lower setbacks to promote density, but usually requires design review for multi-unit buildings.",
        cities: "Palo Alto, Mountain View, Sunnyvale, Santa Clara, San Mateo.",
        mfa: "1.0 - 1.5 FAR",
        setbacks: { front: "15ft", rear: "10ft", side: "5-10ft" },
        height: "35-45 ft",
        lotCoverage: "50-70%"
    },
    {
        code: "R-E",
        name: "Residential Estate",
        category: "Residential",
        uses: ["Estate Single-Family Dwellings", "Equestrian Facilities", "Private Tennis Courts", "ADUs"],
        description: "Large-lot residential zoning prioritizing expansive open space, privacy, and substantial setbacks between neighbors.",
        details: "Similar to Rural Residential but typically found in flatter, more suburban areas rather than hillsides or agricultural fringes.",
        cities: "Atherton, Hillsborough, Woodside, Los Altos.",
        mfa: "5,000 - 10,000+ sq ft",
        setbacks: { front: "40ft", rear: "40ft", side: "20ft" },
        height: "30-35 ft",
        lotCoverage: "15-25%"
    },
    {
        code: "MU",
        name: "Mixed Use",
        category: "Commercial",
        uses: ["Ground Floor Retail", "Upper Floor Apartments", "Live-Work Units", "Professional Offices"],
        description: "Dynamic district designed to blend residential, commercial, and sometimes light industrial uses within a single building or site.",
        details: "Encourages pedestrian-friendly design with zero front setbacks and active street frontages.",
        cities: "San Jose, Oakland, San Francisco, Redwood City, Emeryville.",
        mfa: "1.5 - 3.0 FAR",
        setbacks: { front: "0ft", rear: "10ft", side: "0ft" },
        height: "45-65 ft",
        lotCoverage: "80-100%"
    },
    {
        code: "CG",
        name: "General Commercial",
        category: "Commercial",
        uses: ["Big Box Retail", "Shopping Centers", "Hotels", "Auto Dealerships"],
        description: "Broad commercial zoning for large-scale retail and service operations serving regional populations.",
        details: "Prioritizes vehicular access and large parking lots. Often found along major highways and arterials.",
        cities: "Fremont, San Jose, Milpitas, Santa Clara, San Leandro.",
        mfa: "0.35 - 1.0 FAR",
        setbacks: { front: "15ft", rear: "10ft", side: "0-10ft" },
        height: "45-55 ft",
        lotCoverage: "40-60%"
    },
    {
        code: "CD",
        name: "Downtown Commercial",
        category: "Commercial",
        uses: ["Boutique Retail", "Restaurants & Bars", "High-Density Mixed Use", "Entertainment Venues"],
        description: "Intensive commercial core zoning tailored for historic or primary downtown business districts.",
        details: "Strict architectural standards, minimal to zero setbacks, and often waives or reduces on-site parking requirements in favor of district garages.",
        cities: "Palo Alto, Los Gatos, Burlingame, San Mateo, Walnut Creek.",
        mfa: "2.0 - 4.0 FAR",
        setbacks: { front: "0ft", rear: "0ft", side: "0ft" },
        height: "50-85+ ft",
        lotCoverage: "90-100%"
    },
    {
        code: "IG",
        name: "General Industrial",
        category: "Industrial",
        uses: ["Heavy Manufacturing", "Warehousing & Distribution", "Food Processing", "Waste Processing"],
        description: "Zoning for intensive manufacturing, fabrication, and industrial processes that may generate noise, odor, or heavy traffic.",
        details: "Must be well-separated from residential areas. Lot coverage and FAR limits vary widely based on specific industrial use.",
        cities: "Hayward, San Leandro, South San Francisco, San Jose, Richmond.",
        mfa: "0.50 - 1.5 FAR",
        setbacks: { front: "20ft", rear: "10ft", side: "10ft" },
        height: "50-75 ft",
        lotCoverage: "60-80%"
    },
    {
        code: "P",
        name: "Public / Quasi-Public",
        category: "Special",
        uses: ["Schools & Universities", "Hospitals", "Civic Centers", "Libraries", "Transit Stations"],
        description: "Designated for facilities owned or operated by government agencies or essential non-profit institutions.",
        details: "Development standards are often uniquely negotiated or governed by specific master plans rather than standard zoning tables.",
        cities: "All 92 jurisdictions (e.g., Stanford University lands, County Hospitals).",
        mfa: "Project Specific",
        setbacks: { front: "20ft", rear: "20ft", side: "20ft" },
        height: "Varies widely",
        lotCoverage: "Project Specific"
    },
    {
        code: "PD",
        name: "Planned Development",
        category: "Special",
        uses: ["Custom Master-Planned Communities", "Large Corporate Campuses", "Integrated Mixed-Use Sites"],
        description: "A flexible zoning designation that allows customized regulations for large, complex, or innovative development projects.",
        details: "Requires a comprehensive development plan to be approved by the City Council, replacing standard underlying zoning rules.",
        cities: "San Jose, Cupertino, Mountain View, Sunnyvale, Santa Clara.",
        mfa: "Customized per Plan",
        setbacks: { front: "Custom", rear: "Custom", side: "Custom" },
        height: "Custom",
        lotCoverage: "Custom"
    },
    {
        code: "A",
        name: "Agricultural",
        category: "Agricultural",
        uses: ["Commercial Farming", "Ranches", "Wineries", "Agricultural Processing Facilities"],
        description: "Zoning intended to protect and promote commercial agricultural operations and preserve prime farmland.",
        details: "Extremely large minimum parcel sizes (often 10 to 40+ acres). Residential use is strictly accessory to the farming operation.",
        cities: "Gilroy, Morgan Hill, Half Moon Bay, Pescadero, Brentwood.",
        mfa: "Accessory Only",
        setbacks: { front: "50ft", rear: "50ft", side: "50ft" },
        height: "35 ft (Ag structures exempt)",
        lotCoverage: "5-10%"
    },
    {
        code: "PC",
        name: "Planned Community",
        category: "Special",
        uses: ["Large-Scale Residential Neighborhoods", "Village Centers", "Integrated Parks"],
        description: "Similar to Planned Development but typically applied to massive, multi-phase residential or mixed-use neighborhood expansions.",
        details: "Establishes its own specific plan with detailed design guidelines, infrastructure requirements, and phased build-outs.",
        cities: "Palo Alto, Foster City, Redwood Shores, San Ramon, Dublin.",
        mfa: "Customized per Plan",
        setbacks: { front: "Custom", rear: "Custom", side: "Custom" },
        height: "Custom",
        lotCoverage: "Custom"
    }
];

const ZoningGlossary = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'
    const [activeDetailZone, setActiveDetailZone] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(6);

    const categories = ['All', 'Residential', 'Commercial', 'Industrial', 'Agricultural', 'Special'];

    // Filtered zones
    const filteredZones = useMemo(() => {
        setCurrentPage(1); // Reset page on filter
        return ZONE_DATA.filter(zone => {
            const matchesSearch = 
                zone.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                zone.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                zone.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                zone.uses.some(u => u.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const matchesCategory = selectedCategory === 'All' || zone.category === selectedCategory;
            return matchesSearch && matchesCategory;
        });
    }, [searchTerm, selectedCategory]);

    // Paginated zones
    const paginatedZones = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredZones.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredZones, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(filteredZones.length / itemsPerPage);


    const getCategoryColors = (category) => {
        switch (category) {
            case 'Residential':
                return { bg: 'bg-blue-50/60', text: 'text-blue-600', border: 'border-blue-100', dot: 'bg-blue-500', gradient: 'from-blue-600 to-sky-400' };
            case 'Commercial':
                return { bg: 'bg-emerald-50/60', text: 'text-emerald-600', border: 'border-emerald-100', dot: 'bg-emerald-500', gradient: 'from-emerald-600 to-teal-400' };
            case 'Industrial':
                return { bg: 'bg-purple-50/60', text: 'text-purple-600', border: 'border-purple-100', dot: 'bg-purple-500', gradient: 'from-purple-600 to-indigo-400' };
            case 'Agricultural':
                return { bg: 'bg-amber-50/60', text: 'text-amber-600', border: 'border-amber-100', dot: 'bg-amber-500', gradient: 'from-amber-600 to-yellow-400' };
            default:
                return { bg: 'bg-slate-50/60', text: 'text-slate-600', border: 'border-slate-100', dot: 'bg-slate-500', gradient: 'from-slate-700 to-slate-500' };
        }
    };

    return (
        <div className="relative space-y-8 min-h-screen pb-12 pr-1 no-scrollbar select-none w-full max-w-full overflow-x-hidden">
            {/* Ambient Background Blur Effect */}
            <div className="absolute top-0 right-1/4 w-96 h-96 bg-indigo-200/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-1/3 left-10 w-80 h-80 bg-emerald-200/10 rounded-full blur-3xl pointer-events-none" />

            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 pl-4 relative z-10">
                <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                        <div className="w-1.5 h-8 bg-indigo-600 rounded-full"></div>
                        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight leading-none">
                            Zoning <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 to-indigo-400">Glossary</span>
                        </h1>
                    </div>
                    <p className="text-slate-500 font-bold text-xs uppercase tracking-[0.15em]">
                        Administrative Land Use Directory • 92 Jurisdictions
                    </p>
                </div>

                {/* Grid/List View Toggles */}
                <div className="flex items-center bg-white p-1 rounded-2xl border border-slate-200/80 shadow-xs">
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2.5 rounded-xl transition-all ${
                            viewMode === 'grid' 
                                ? 'bg-slate-900 text-white shadow-md' 
                                : 'text-slate-400 hover:text-slate-700'
                        }`}
                    >
                        <Grid className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setViewMode('table')}
                        className={`p-2.5 rounded-xl transition-all ${
                            viewMode === 'table' 
                                ? 'bg-slate-900 text-white shadow-md' 
                                : 'text-slate-400 hover:text-slate-700'
                        }`}
                    >
                        <List className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Modern Control Bar */}
            <div className="flex flex-col lg:flex-row gap-4 bg-white/90 backdrop-blur-md p-5 rounded-3xl border border-slate-200/80 shadow-xs relative z-10">
                {/* Search Input */}
                <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Search codes, designations, or permitted uses..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-11 pr-4 py-3 bg-slate-50/50 border border-slate-200 rounded-2xl text-sm font-bold text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
                    />
                </div>

                {/* Filter Pills */}
                <div className="flex items-center space-x-1.5 overflow-x-auto no-scrollbar py-0.5">
                    {categories.map((cat) => (
                        <button
                            key={cat}
                            onClick={() => setSelectedCategory(cat)}
                            className={`px-4 py-2 rounded-xl text-[10px] font-extrabold uppercase tracking-widest transition-all ${
                                selectedCategory === cat
                                    ? 'bg-indigo-600 text-white shadow-md shadow-indigo-500/20'
                                    : 'bg-slate-50 text-slate-400 hover:text-slate-700 border border-slate-200/60 hover:bg-slate-100'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content Display (Grid View / Table View) */}
            <div className="relative z-10 w-full min-w-0 max-w-full">
                <AnimatePresence mode="wait">
                    {viewMode === 'grid' ? (
                        <motion.div
                            key="grid"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full"
                        >
                            {paginatedZones.map((zone) => {
                                const colors = getCategoryColors(zone.category);
                                return (
                                    <motion.div
                                        key={zone.code}
                                        whileHover={{ y: -5, shadow: "0 20px 25px -5px rgb(0 0 0 / 0.05)" }}
                                        onClick={() => setActiveDetailZone(zone)}
                                        className="bg-white rounded-3xl border border-slate-200/80 p-6 flex flex-col justify-between cursor-pointer transition-all duration-300 relative group overflow-hidden"
                                    >
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-slate-50 to-transparent group-hover:from-indigo-50/40 rounded-bl-full transition-colors" />
                                        
                                        <div className="space-y-4">
                                            {/* Tag & Category */}
                                            <div className="flex items-center justify-between">
                                                <span className={`inline-flex px-3 py-1 bg-gradient-to-r ${colors.gradient} text-white text-[11px] font-black rounded-xl uppercase tracking-wider shadow-sm`}>
                                                    {zone.code}
                                                </span>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[8px] font-black uppercase tracking-wider ${colors.bg} ${colors.text} ${colors.border}`}>
                                                    <div className={`w-1 h-1 rounded-full ${colors.dot} mr-1.5`} />
                                                    {zone.category}
                                                </span>
                                            </div>

                                            {/* Name & Short description */}
                                            <div className="space-y-1.5">
                                                <h3 className="text-base font-black text-slate-800 tracking-tight group-hover:text-indigo-600 transition-colors">{zone.name}</h3>
                                                <p className="text-xs font-bold text-slate-400 line-clamp-3 leading-relaxed">{zone.description}</p>
                                            </div>
                                        </div>

                                        {/* Lower Info */}
                                        <div className="pt-5 mt-5 border-t border-slate-100 flex items-center justify-between">
                                            <span className="text-[10px] font-extrabold text-slate-300 uppercase tracking-wider">{zone.uses.length} Allowed Uses</span>
                                            <div className="flex items-center space-x-1 text-xs font-black text-indigo-600 group-hover:translate-x-1 transition-transform">
                                                <span>Details</span>
                                                <ArrowRight className="w-3.5 h-3.5" />
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    ) : (
                        /* Table View matching UserBase */
                        <motion.div
                            key="table"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            className="flex-1 min-h-0 min-w-0 w-full max-w-full bg-white rounded-[2.5rem] border border-slate-200/60 shadow-2xl shadow-slate-200/40 flex flex-col overflow-hidden relative"
                        >
                            <div className="overflow-x-auto flex-1 no-scrollbar relative w-full min-w-0">
                                <table className="w-full text-left border-collapse min-w-[1000px]">
                                    <thead className="sticky top-0 z-40">
                                        <tr className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 border-b border-white/10">
                                            <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Zoning District</th>
                                            <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Category</th>
                                            <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Max Floor Area</th>
                                            <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Setbacks & Height</th>
                                            <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em]">Lot Coverage</th>
                                            <th className="px-10 py-6 text-[11px] font-black text-white uppercase tracking-[0.25em] text-right">Details</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100/50">
                                        {paginatedZones.map((zone, idx) => {
                                            const colors = getCategoryColors(zone.category);
                                            return (
                                                <motion.tr 
                                                    key={zone.code} 
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: idx * 0.05 }}
                                                    onClick={() => setActiveDetailZone(zone)}
                                                    className="group transition-all cursor-pointer relative hover:bg-slate-50/50"
                                                >
                                                    <td className="px-10 py-4 relative z-10">
                                                        <div className="flex items-center gap-5">
                                                            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colors.gradient} flex items-center justify-center text-white shadow-sm overflow-hidden relative`}>
                                                                <span className="font-black text-sm uppercase tracking-widest">{zone.code}</span>
                                                            </div>
                                                            <div>
                                                                <p className="text-xs font-black text-slate-900 leading-none tracking-tight group-hover:text-indigo-600 transition-colors">{zone.name}</p>
                                                                <div className="flex flex-wrap gap-1 mt-2">
                                                                    {zone.uses.slice(0, 2).map((use, i) => (
                                                                        <span key={i} className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded leading-none">{use}</span>
                                                                    ))}
                                                                    {zone.uses.length > 2 && <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded leading-none">+{zone.uses.length - 2} more</span>}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-10 py-4 relative z-10">
                                                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${colors.bg} ${colors.text} ${colors.border}`}>
                                                            <div className={`w-1.5 h-1.5 rounded-full ${colors.dot}`} />
                                                            {zone.category}
                                                        </div>
                                                    </td>
                                                    <td className="px-10 py-4 relative z-10">
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-black text-slate-900 tracking-tight">{zone.mfa || "N/A"}</span>
                                                            <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Base Allocation</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-10 py-4 relative z-10">
                                                        <div className="w-[180px]">
                                                            <div className="grid grid-cols-3 gap-3">
                                                                <div className="flex flex-col">
                                                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Front</span>
                                                                    <span className="text-[11px] font-black text-slate-800 tracking-tight">{zone.setbacks?.front || '-'}</span>
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Side</span>
                                                                    <span className="text-[11px] font-black text-slate-800 tracking-tight">{zone.setbacks?.side || '-'}</span>
                                                                </div>
                                                                <div className="flex flex-col">
                                                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Rear</span>
                                                                    <span className="text-[11px] font-black text-slate-800 tracking-tight">{zone.setbacks?.rear || '-'}</span>
                                                                </div>
                                                            </div>
                                                            <div className="h-px bg-slate-100 w-full my-2.5" />
                                                            <div className="flex items-center justify-between">
                                                                <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-rose-400 shadow-[0_0_8px_rgba(251,113,133,0.4)]" />
                                                                    Max Height
                                                                </span>
                                                                <span className="text-[10px] font-black text-rose-600 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100">{zone.height || '-'}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-10 py-4 relative z-10">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                                            <span className="text-xs font-black text-slate-900 tracking-tight">{zone.lotCoverage || "N/A"}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-10 py-4 relative z-10 text-right">
                                                        <div className="flex items-center justify-end">
                                                            <div className="w-8 h-8 rounded-xl bg-indigo-50/50 flex items-center justify-center text-indigo-400 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
                                                                <Eye className="w-4 h-4" />
                                                            </div>
                                                        </div>
                                                    </td>
                                                </motion.tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* High-Standard Pagination Controller */}
                            {totalPages > 1 && (
                                <div className="px-10 py-6 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
                                    <div className="flex items-center gap-6">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                            Showing <span className="text-slate-900">{(currentPage - 1) * itemsPerPage + 1}-{Math.min(filteredZones.length, currentPage * itemsPerPage)}</span> of <span className="text-slate-900">{filteredZones.length}</span> results
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:shadow-md disabled:opacity-30 disabled:hover:shadow-none transition-all"
                                        >
                                            <ChevronLeft className="w-4 h-4" />
                                        </button>

                                        <div className="flex items-center px-2 gap-1">
                                            {Array.from({ length: totalPages }, (_, i) => {
                                                const pageNum = i + 1;
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`w-8 h-8 rounded-xl text-[10px] font-black transition-all ${currentPage === pageNum
                                                            ? 'bg-slate-900 text-white shadow-lg shadow-slate-200'
                                                            : 'text-slate-400 hover:bg-white hover:text-slate-900'
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            className="p-2 rounded-xl border border-slate-200 bg-white text-slate-400 hover:text-slate-900 hover:shadow-md disabled:opacity-30 disabled:hover:shadow-none transition-all"
                                        >
                                            <ChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    )}
                </AnimatePresence>

                {filteredZones.length === 0 && (
                    <div className="text-center py-20 bg-white rounded-3xl border border-slate-200">
                        <FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest">No matching zones detected</p>
                    </div>
                )}
            </div>

            {/* Pagination Controls for Grid View */}
            {viewMode === 'grid' && totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-3xl border border-slate-200 shadow-xs relative z-10">
                    <div className="flex items-center space-x-3 text-xs font-bold text-slate-400">
                        <span>Show</span>
                        <select 
                            value={itemsPerPage} 
                            onChange={(e) => {
                                setItemsPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                            className="bg-slate-50 border border-slate-200 py-1.5 px-2 rounded-xl text-slate-700 font-extrabold focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        >
                            {[6, 12, 18].map(size => (
                                <option key={size} value={size}>{size} per page</option>
                            ))}
                        </select>
                        <span className="hidden sm:inline">| Showing {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredZones.length)} of {filteredZones.length} entries</span>
                    </div>

                    <div className="flex items-center space-x-1.5">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-xl disabled:opacity-40 disabled:hover:bg-slate-50 border border-slate-200/60 transition-colors"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        
                        {[...Array(totalPages)].map((_, i) => {
                            const page = i + 1;
                            return (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-black transition-all ${
                                        currentPage === page
                                            ? 'bg-slate-900 text-white shadow-md'
                                            : 'bg-slate-50 hover:bg-slate-100 text-slate-500 border border-slate-200/60'
                                    }`}
                                >
                                    {page}
                                </button>
                            );
                        })}

                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            className="p-2 bg-slate-50 hover:bg-slate-100 text-slate-500 rounded-xl disabled:opacity-40 disabled:hover:bg-slate-50 border border-slate-200/60 transition-colors"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Slider Side Drawer for Details */}
            <AnimatePresence>
                {activeDetailZone && (
                    <>
                        {/* Overlay */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 0.3 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setActiveDetailZone(null)}
                            className="fixed inset-0 bg-slate-900 z-50 backdrop-blur-xs"
                        />

                        {/* Drawer */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 220 }}
                            className="fixed inset-y-0 right-0 w-full max-w-xl bg-white shadow-2xl z-50 border-l border-slate-200 flex flex-col justify-between"
                        >
                            {/* Drawer Header */}
                            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                    <span className={`inline-flex px-3 py-1 bg-gradient-to-r ${getCategoryColors(activeDetailZone.category).gradient} text-white text-xs font-black rounded-lg uppercase tracking-wider`}>
                                        {activeDetailZone.code}
                                    </span>
                                    <span className="text-slate-400 font-extrabold text-[10px] uppercase tracking-widest">{activeDetailZone.category}</span>
                                </div>
                                <button 
                                    onClick={() => setActiveDetailZone(null)}
                                    className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition-all"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Drawer Scrollable Content */}
                            <div className="flex-1 overflow-y-auto no-scrollbar p-6 space-y-6">
                                <div className="space-y-1">
                                    <h2 className="text-xl font-black text-slate-900 tracking-tight leading-snug">{activeDetailZone.name}</h2>
                                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active municipal code definition</p>
                                </div>

                                {/* Detailed Card */}
                                <div className="space-y-1.5">
                                    <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <Info className="w-3.5 h-3.5 text-indigo-500" />
                                        <span>Zoning Code Definition</span>
                                    </div>
                                    <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-100 text-xs font-bold text-slate-500 leading-relaxed shadow-xs">
                                        {activeDetailZone.description}
                                    </div>
                                </div>

                                {/* Parameters */}
                                <div className="space-y-1.5">
                                    <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <ShieldAlert className="w-3.5 h-3.5 text-indigo-500" />
                                        <span>Typical Development Parameters</span>
                                    </div>
                                    <div className="bg-slate-50/50 p-5 rounded-2xl border border-slate-100 text-xs font-bold text-slate-500 leading-relaxed shadow-xs">
                                        {activeDetailZone.details}
                                    </div>
                                </div>

                                {/* Permitted Uses Checklist */}
                                <div className="space-y-2.5">
                                    <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                        <CheckCircle className="w-3.5 h-3.5 text-indigo-500" />
                                        <span>Primary Allowed Land Uses</span>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2">
                                        {activeDetailZone.uses.map((use, i) => (
                                            <div key={i} className="flex items-center space-x-2.5 bg-white border border-slate-100 p-3 rounded-xl shadow-xs">
                                                <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full flex-shrink-0" />
                                                <span className="text-xs font-black text-slate-700 tracking-tight">{use}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Drawer Footer */}
                            <div className="p-6 border-t border-slate-100 bg-slate-50/50">
                                <div className="flex items-start space-x-3">
                                    <HelpCircle className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Regional Applicability</h4>
                                        <p className="text-xs font-bold text-slate-600 leading-relaxed">{activeDetailZone.cities}</p>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ZoningGlossary;
